import React, { useReducer, useState } from 'react';
import { Alert, Button, CheckboxField, LoadingSpinner, ModalDialog } from 'vetrf-ui';
import { FormContainer } from 'components/FormContainer';
import { useUserDataContext } from 'components/UserDataContext/context';
import { exportExcelApi } from 'api/exportExcel';
import { DefaultFindFnParams, ExcelFields, FindFnParams } from '../../types/api';
import { ExportingFields } from './ExportingFields';
import { dragReducer } from './utils';
import { ModalForm } from './types';
import { TemplateBlock } from './TemplateBlock';
import styles from './ExportExcelModal.module.scss';

type RegistrySortingProps<T> = {
  onClose: () => void;
  excelFields: ExcelFields[];
  findParams?: FindFnParams<T>;
  groupByField?: ExcelFields;
  exportMethod?: (data: DefaultFindFnParams) => Promise<void>;
};

export const ExportExcelModal = <T,>({
  onClose,
  excelFields,
  findParams,
  groupByField,
  exportMethod = exportExcelApi.getExcel,
}: RegistrySortingProps<T>) => {
  const { userData } = useUserDataContext();
  const [loading, setLoading] = useState(false);
  const [excelItems, dispatch] = useReducer(dragReducer, {
    items: excelFields.filter((item) => !item.enable),
    selectedItems: excelFields.filter((item) => item.enable),
  });

  const handleSubmit = (values: ModalForm) => {
    if (findParams) {
      setLoading(true);
      const formattedValue = {
        ...findParams,
        pageDetails: {
          ...findParams.pageDetails,
          fullList: true,
        },
        fields: excelItems.selectedItems,
        groupByField: values?.check === false ? groupByField : undefined,
      };
      return exportMethod(formattedValue).finally(() => setLoading(false));
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit} className={styles.formWrapper}>
      <ModalDialog
        header="Поля для просмотра и печати"
        headerClass="modal-header modal-large"
        onClose={onClose}
        footer={
          <>
            <Button
              type="submit"
              color="success"
              icon="download"
              iconColor="white"
              iconSize={100}
              size="sm"
              text="Скачать Excel"
              disabled={loading}
            />
            <Button
              color="grey"
              icon="times"
              iconColor="white"
              iconSize={100}
              onClick={onClose}
              size="sm"
              text="Отмена"
            />
          </>
        }
      >
        <Alert messageType="info">
          Выберите поля записей, которые будут экспортированы в Excel перетаскиванием из одного
          списка в другой.
        </Alert>
        <div className="row">
          {userData && findParams?.entryType && findParams?.productType && (
            <div className="col-xs-12">
              <TemplateBlock
                excelItems={excelItems}
                defaultFields={excelFields}
                dispatch={dispatch}
                entryType={findParams.entryType}
                productType={findParams.productType}
              />
            </div>
          )}

          <div className="col-xs-12">
            <hr />
          </div>

          {groupByField && (
            <div className={`col-xs-12 col-sm-6 ${styles.groupCheck}`}>
              <CheckboxField
                fieldClassName="text-right px-5"
                labelClassName="control-label"
                labelText={`Группировать по полю ${groupByField.text}`}
                name="check"
                defaultValue
              />
            </div>
          )}

          <div className="col-xs-12">
            <ExportingFields excelItems={excelItems} dispatch={dispatch} />
          </div>
        </div>

        {loading && (
          <Alert messageType="info" className="mt-15 mb-0">
            <LoadingSpinner color="blue" icon="spinner" spin="spin" text="Загрузка..." />
          </Alert>
        )}
      </ModalDialog>
    </FormContainer>
  );
};
